export type PropertyOwnerLink = {
  property_id: number | string | null;
  user_id: string | null;
};

export type OwnerDemoFlag = { id: string; is_demo_account: boolean | null };

/**
 * Properties whose analytics inventory is sales-demo data — in practice the
 * hidden demo property behind COL-447's owner <-> tenant switch.
 *
 * A property qualifies only when EVERY account linked to it is flagged
 * `profiles.is_demo_account`. Note what that rule deliberately is NOT:
 *
 *  - NOT the email-based `isTestAccount` rule that filters bookings and
 *    payments. `property_owners` is a portal-ACCESS grant ("this account may
 *    open this property"), not unit ownership — see
 *    `20260915190100_admin_link_owner_apartment.sql`, and note that unit
 *    ownership lives on `apartments.owner_user_id`. A real building routinely
 *    has a @coliviq.de login granted against it (onboarding, support) while
 *    its apartments and tenants are real, so "every linked account is a
 *    CoLiviq account" would drop that building's rooms out of the occupancy
 *    DENOMINATOR while its real tenants stayed in the numerator — occupancy
 *    understated and `availableRooms` going negative.
 *  - NOT `is_hidden`: new rooms and apartments are created hidden and unhidden
 *    once they have photos and a price, so `is_hidden` is a publishing
 *    lifecycle state on real inventory.
 *
 * The demo flag is set deliberately, by an admin, on the one account the demo
 * belongs to, so this cannot capture a real building by accident.
 */
export function demoOwnedPropertyIds(
  links: PropertyOwnerLink[],
  ownersById: Map<string, OwnerDemoFlag>
): string[] {
  /** property id -> whether every linked account so far is a demo account. */
  const allDemo = new Map<string, boolean>();

  for (const link of links) {
    if (!link.user_id) continue;
    const propertyId = String(link.property_id ?? "");
    if (!propertyId) continue;

    // An account we could not resolve is treated as real, so an unreadable
    // profile can never cause real inventory to disappear.
    const isDemo = ownersById.get(link.user_id)?.is_demo_account === true;

    allDemo.set(propertyId, (allDemo.get(propertyId) ?? true) && isDemo);
  }

  return [...allDemo.entries()]
    .filter(([, everyOwnerIsDemo]) => everyOwnerIsDemo)
    .map(([propertyId]) => propertyId);
}

/**
 * Drop the rows that belong to an excluded demo property.
 *
 * Deliberately shared between the occupancy DENOMINATOR (the room set) and its
 * NUMERATOR (the bookings counted as occupied). Those two were filtered by
 * different predicates once — rooms by this demo rule, bookings by the
 * email-based `isTestAccount` — and nothing ties an owner account's demo flag
 * to the EMAIL of a tenant renting one of its rooms. A demo property housing a
 * tenant on an ordinary address therefore lost its rooms from the denominator
 * while its booking stayed in the numerator, which is how
 * `availableRooms = totalRooms - occupiedRooms` goes negative. One helper with
 * two accessors keeps the two sides agreeing by construction rather than by
 * coincidence.
 *
 * A row whose property cannot be resolved counts as NOT excluded, matching
 * `demoOwnedPropertyIds`: an unreadable row can never make inventory vanish.
 */
export function withoutDemoProperties<T>(
  rows: readonly T[],
  excludedPropertyIds: ReadonlySet<string>,
  propertyIdOf: (row: T) => unknown
): T[] {
  if (excludedPropertyIds.size === 0) return rows as T[];
  return rows.filter(
    (row) => !excludedPropertyIds.has(String(propertyIdOf(row) ?? ""))
  );
}
